import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Architecture principles",
  "description": "Learn how to design a serverless system that keeps working in the face of errors",
  "image": "./img/architecture-principles.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "architecture-principles"
    }}>{`Architecture principles`}</h1>
    <p><img parentName="p" {...{
        "src": "/7c17de2f1dbc0799a2dd6c5135dbe399/architecture-principles.svg",
        "alt": null
      }}></img></p>
    <p>{`What do you do when 3% of requests fail?`}</p>
    <p>{`That was my reality one painful day in September. Deploy new feature, pat yourself on the back, go to lunch. A big feature – done. Nothing can touch you. 💪`}</p>
    <p>{`Ding.`}</p>
    <p><em parentName="p">{`A Slack message? Who's working through lunch this time ...`}</em></p>
    <p>{`Ding. Ding.`}</p>
    <p><em parentName="p">{`Weird, that's a lot of messages for lunchtime ...`}</em></p>
    <p>{`BZZZ`}</p>
    <blockquote>
      <p parentName="blockquote">{`Everything is on fire. High API error rate 🔥`}<br />{`
~ an alarm SMS`}</p>
    </blockquote>
    <p>{`Our system sent alarms to Slack. Serious alarms to SMS.`}</p>
    <p>{`The `}<em parentName="p">{`high API error rate`}</em>{` was the biggest alarm. A catch-all that triggered when you can't be sure the more specific alarms even work.`}</p>
    <p>{`Back to my desk, my heart sank: 3% of all API requests were failing. Reasons unknown.`}</p>
    <p>{`Every user action, every background process on the web, on iOS `}<em parentName="p">{`and`}</em>{` on Android, every time you opened the site or accessed the app 👉 3% chance of failure. At our usual 10 requests per second, that's 18 errors every minute!`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media2.giphy.com/media/gK5sT0T6ZoHvx00Zrs/giphy-loop.mp4?cid=4ac046a217ooh7fk6xxdjlem97bnt6s4xuvly0cw5qf33dd8&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Wanna know the best part?`}</p>
    <p>{`Nobody noticed. ✌️`}</p>
    <p>{`I knew something was wrong because of that SMS. The system kept hobbling along. Slower, in pain, but getting the job done.`}</p>
    <p>{`How can a system with 18 failures per minute keep working?`}</p>
    <h2 {...{
      "id": "everything-fails"
    }}>{`Everything fails`}</h2>
    <p><em parentName="p">{`The`}</em>{` design principle behind every backend architecture states:`}</p>
    <ol>
      <li parentName="ol">{`Everything can and will fail`}</li>
      <li parentName="ol">{`Your system should work anyway`}</li>
      <li parentName="ol">{`Make failures easy to fix`}</li>
    </ol>
    <div id="lock" />
    <p>{`In 2011 Netflix forced engineers to think about this with the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Chaos_engineering"
      }}>{`Chaos Monkey`}</a>{`. They wanted to `}<em parentName="p">{`"move from a development model that assumed no breakdowns to a model where breakdowns were considered to be inevitable"`}</em>{`.`}</p>
    <p>{`The Chaos Monkey makes that happen by introducing random failures to production environments. Servers down, databases unresponsive, message queues failing. `}</p>
    <p>{`Reality is going to be your chaos monkey. Plan for it. 😉`}</p>
    <h2 {...{
      "id": "how-statistics-play-against-you"
    }}>{`How statistics play against you`}</h2>
    <p>{`At scale, you're playing against statistics. A one in a million error rate, at 10 requests per second, means an error every day.`}</p>
    <p>{`Not a big deal, but if that error happens in your payment flow and you double charge a user ... they'll care.`}</p>
    <p>{`What do statistics mean for you and your architecture? `}</p>
    <p><a parentName="p" {...{
        "href": "https://aws.amazon.com/lambda/sla/"
      }}>{`AWS Lambda`}</a>{` guarantees a 99.95% monthly uptime percentage. That's your base error rate.`}</p>
    <p><strong parentName="p">{`Even if you write the most perfect code, you can't have a lower error rate than your platform.`}</strong></p>
    <p>{`What does `}<em parentName="p">{`99.95% monthly uptime percentage`}</em>{` mean in practice?`}</p>
    <blockquote>
      <p parentName="blockquote">{`“Monthly Uptime Percentage” for a given AWS region is calculated as the average of the Availability for all 5-minute intervals in a monthly billing cycle. Monthly Uptime Percentage measurements exclude downtime resulting directly or indirectly from any Lambda SLA Exclusion.`}</p>
      <p parentName="blockquote">{`“Availability” is calculated for each 5-minute interval as the percentage of Requests processed by Lambda that do not fail with Errors and relate solely to the provisioned Lambda functions. If you did not make any Requests in a given 5-minute interval, that interval is assumed to be 100% available.`}</p>
    </blockquote>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media4.giphy.com/media/kgEeS3motMqpW/giphy-loop.mp4?cid=4ac046a2y27ciu3hm2r2zydard0hytwf1nggccj3s78iuj73&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h3 {...{
      "id": "lets-run-the-numbers"
    }}>{`Let's run the numbers`}</h3>
    <p>{`There are `}<inlineCode parentName="p">{`24*60/5 = 288`}</inlineCode>{` 5-minute intervals in a day. An average of 99.95% means that some intervals have lower availability, some higher. None go higher than 100%.`}</p>
    <p>{`The hidden message is that you're going to see bursts of failures. A bad 5-minute period in a sea of 100% green. Outliers pull the average down.`}</p>
    <p>{`Let's assume an even error rate to simplify the math.`}</p>
    <p>{`At 1 request per 5 minutes, you get `}<inlineCode parentName="p">{`288 * 0.9995 = 287.86`}</inlineCode>{` successful requests. Round up to 288. All good.`}</p>
    <p>{`But at 1 per minute, you're down to `}<inlineCode parentName="p">{`1439`}</inlineCode>{` successful requests. An error per day.`}</p>
    <p>{`It gets worse from there.`}</p>
    <p>{`I once built a large system running at a 0.092% failure rate. Lower than the `}<em parentName="p">{`error`}</em>{` rate of the platform because of the architecture.`}</p>
    <p>{`Pretty good eh?`}</p>
    <p><em parentName="p">{`1069 errors per day.`}</em></p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media0.giphy.com/media/p4cqQ0gUIMcU0/giphy-loop.mp4?cid=4ac046a2ng72mls5c85v2v8s0g9bqy297p8ve1svg1iojhlu&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p><em parentName="p">{`PS: failure rate means the system never manages to process a request, error rate means individual, possibly recoverable, errors along the way`}</em></p>
    <h2 {...{
      "id": "how-to-design-a-resilient-architecture"
    }}>{`How to design a resilient architecture`}</h2>
    <p><a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Resilience_(engineering_and_construction)"
      }}>{`Resilience`}</a>{` is key. Designing your system to withstand shocks and errors. To work as a whole when individual pieces fail.`}</p>
    <p>{`Your goal is to:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`isolate errors`}</strong>{` when an error happens, confine it to the smallest area possible without damaging the rest of your system`}</li>
      <li parentName="ul"><strong parentName="li">{`make operations replayable`}</strong>{` triggering the same operation multiple times must produce the same result`}</li>
      <li parentName="ul"><strong parentName="li">{`retry until success`}</strong>{` errors are often transient and go away when you try again`}</li>
      <li parentName="ul"><strong parentName="li">{`make your system debuggable`}</strong>{` store enough information about your actions so that you can look back later`}</li>
      <li parentName="ul"><strong parentName="li">{`remove unprocessable requests`}</strong>{` sometimes requests can't be processed. Make sure these don't kill your system or get in the way of valid requests`}</li>
      <li parentName="ul"><strong parentName="li">{`alert the engineer when something is wrong`}</strong>{` errors can be part of normal operations. When there's too many, make your system cry out in pain and tell you where it hurts`}</li>
    </ul>
    <p>{`You can achieve all that with a few steps.`}</p>
    <h3 {...{
      "id": "small-isolated-replayable-operations"
    }}>{`Small, isolated, replayable operations`}</h3>
    <p>{`Think of your task as a pipeline. `}</p>
    <p>{`A request comes in. You do X, then you do Y, then Z, then the result comes out. The request is your input, the result is your output.`}</p>
    <p>{`Like Henry Ford's famous assembly line 👉 steel comes in the factory on one end, cars leave on the other. `}</p>
    <p>{`For max resilience, make each operation follow this algorithm:`}</p>
    <ul>
      <li parentName="ul">{`get request`}</li>
      <li parentName="ul">{`check if request already processed`}</li>
      <li parentName="ul">{`if processed, finish`}</li>
      <li parentName="ul">{`if not, do your thing`}</li>
      <li parentName="ul">{`trigger the next step`}</li>
      <li parentName="ul">{`mark request as processed`}</li>
    </ul>
    <p><strong parentName="p">{`Every request needs an identifier.`}</strong>{` Easiest if it's a unique `}<inlineCode parentName="p">{`id`}</inlineCode>{` of a database row. `}</p>
    <p>{`Having a way to answer `}<em parentName="p">{`"Did I process this yet?"`}</em>{` gives you replayability. Call the same action with the same request twice and nothing happens.`}</p>
    <p>{`Easiest way to achieve this, is a lookup table. I like to put a `}<inlineCode parentName="p">{`processed`}</inlineCode>{` flag in each database row. Lets you pass a reference to the row between actions. Each does its thing and changes the flag.`}</p>
    <p><strong parentName="p">{`Each action does one thing and one thing only.`}</strong></p>
    <p>{`Because each action performs one step in the process, you can look at the `}<inlineCode parentName="p">{`processed`}</inlineCode>{` flags to see what's up.`}</p>
    <p>{`Debugging becomes easier. You can test steps in the process on their own. Like a function call.`}</p>
    <p><strong parentName="p">{`Isolation comes from decoupling`}</strong>{` `}</p>
    <p>{`Isolation ensures that an error in one action doesn't impact others. `}<em parentName="p">{`That`}</em>{` action fails and the pipeline might get backed up, but other actions keep working fine.`}</p>
    <p>{`And because you have replayability, you can retry until your action succeeds. ✌️`}</p>
    <h3 {...{
      "id": "retry-until-success"
    }}>{`Retry until success`}</h3>
    <p>{`Fire an action, get success or fail. If success, remove message from the queue and move on. If it fails, put message back on queue and try again.`}</p>
    <p>{`Errors can be temporary. Engineers will fix the bug. `}</p>
    <p>{`If it's a hardware problem, you can wait until your function runs on a different physical machine. Yay serverless.`}</p>
    <p>{`You can keep retrying until success because actions are safely replayable.`}</p>
    <p>{`To be safe, I like to have a `}<strong parentName="p">{`cleanup worker`}</strong>{` that goes through the database every few hours and checks for rows that failed to process and stopped retrying. `}</p>
    <p>{`Catches errors in the retry system itself. ✌️`}</p>
    <p><em parentName="p">{`PS: more on queues and how this works in `}<a parentName="em" {...{
          "href": "/serverless-elements"
        }}>{`the chapter on serverless elements`}</a></em></p>
    <h3 {...{
      "id": "a-debuggable-system-is-a-good-system"
    }}>{`A debuggable system is a good system`}</h3>
    <p>{`Small actions that store a `}<em parentName="p">{`"Yes I Did It"`}</em>{` state for each piece of data are easier to debug. You can look at your database to see which step failed.`}</p>
    <p>{`Replayability lets you trigger the failed action for that data and see what happens.`}</p>
    <p>{`Since actions are functions and your messages are plain data, you can test locally. Unit tests are great, running production code on production data in your terminal, that's wow.`}</p>
    <p>{`And if that's not enough, add logging. A well-placed `}<inlineCode parentName="p">{`console.log`}</inlineCode>{` can do wonders.`}</p>
    <h2 {...{
      "id": "conclusion"
    }}>{`Conclusion`}</h2>
    <blockquote>
      <p parentName="blockquote">{`Build your system out of small isolated pieces that talk to each other via queues.`}</p>
    </blockquote>
    <p>{`Next chapter we dive into queues and lambdas, and talk about how to tie them together into a system.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      